/**
 * Composants de la charte Emily Editions.
 * Maquette de reference : Boutique Emily Editions (Claude Design).
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Revele ses descendants marques [data-ee-reveal] quand ils entrent dans le viewport.
 * Usage : <ee-reveal> ... <div data-ee-reveal></div> ... </ee-reveal>
 */
class EmilyReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const cibles = this.querySelectorAll('[data-ee-reveal]');

    if (reduceMotion()) {
      cibles.forEach((cible) => cible.classList.add('ee-visible'));
      return;
    }

    this.#observer = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((entree) => {
          // Un element deja depasse vers le haut ne doit pas rester masque
          if (!entree.isIntersecting && entree.boundingClientRect.bottom > 0) return;
          entree.target.classList.add('ee-visible');
          this.#observer?.unobserve(entree.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    cibles.forEach((cible, index) => {
      /** @type {HTMLElement} */ (cible).style.transitionDelay = `${(index % 4) * 0.08}s`;
      this.#observer?.observe(cible);
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

/**
 * Lookbook epingle : la piste defile horizontalement pendant que la section reste collee.
 * Usage : <ee-lookbook> avec un enfant [data-ee-track].
 */
class EmilyLookbook extends HTMLElement {
  #controller = new AbortController();

  /** @type {number} */
  #frame = 0;

  get #track() {
    return this.querySelector('[data-ee-track]');
  }

  connectedCallback() {
    if (reduceMotion()) return;

    const { signal } = this.#controller;
    const onScroll = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#positionner();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    this.#positionner();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #positionner() {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const rect = this.getBoundingClientRect();
    const course = this.offsetHeight - window.innerHeight;
    const avancement = Math.min(1, Math.max(0, -rect.top / (course || 1)));
    const distance = Math.max(0, track.scrollWidth - window.innerWidth + 40);

    track.style.transform = `translate3d(${(-avancement * distance).toFixed(1)}px, 0, 0)`;
  }
}

/**
 * Volet rose qui balaie la section une seule fois, a la premiere apparition.
 * Usage : <ee-wipe> avec un enfant [data-ee-voile].
 */
class EmilyWipe extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const voile = this.querySelector('[data-ee-voile]');
    if (!(voile instanceof HTMLElement)) return;

    if (reduceMotion()) {
      voile.remove();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entrees) => {
        if (!entrees.some((entree) => entree.isIntersecting)) return;
        voile.style.transition = 'transform 1.5s cubic-bezier(0.76, 0, 0.24, 1)';
        voile.style.transform = 'translateX(102%)';
        this.#observer?.disconnect();
      },
      { threshold: 0.25 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

/**
 * Diaporama en fondu enchaine.
 * Usage : <ee-diaporama data-delai="3000"> avec des enfants [data-ee-vue].
 */
class EmilyDiaporama extends HTMLElement {
  /** @type {number} */
  #minuteur = 0;

  /** @type {number} */
  #fin = 0;

  #index = 0;

  connectedCallback() {
    const vues = this.querySelectorAll('[data-ee-vue]');
    if (vues.length < 2 || reduceMotion()) return;

    const delai = Number(this.dataset.delai) || 3000;
    const fondu = Number(this.dataset.fondu) || 700;

    // Rang croissant : chaque nouvelle photo passe au-dessus de la precedente,
    // quel que soit son ordre dans le HTML. Sans cela, le retour a la premiere
    // photo la fait passer dessous et declenche un second fondu a l'envers.
    let rang = 1;
    vues[this.#index].style.zIndex = '0';

    // Les photos suivantes attendent dans un <template> (voir ee-hero.liquid) :
    // la seconde se charge une fois la page prete, puis chacune pendant que
    // la precedente est a l'ecran.
    if (document.readyState === 'complete') {
      this.#deplier(vues[1]);
    } else {
      window.addEventListener('load', () => this.#deplier(vues[1]), { once: true });
    }

    this.#minuteur = window.setInterval(() => {
      // Onglet en arriere-plan : inutile de faire tourner le diaporama
      if (document.hidden) return;

      const sortante = vues[this.#index];
      const suivant = (this.#index + 1) % vues.length;
      const entrante = vues[suivant];

      // Filet de securite si la page n'avait pas fini de charger
      this.#deplier(entrante);
      this.#deplier(vues[(suivant + 1) % vues.length]);

      rang += 1;
      entrante.style.zIndex = String(rang);
      entrante.classList.add('est-active');
      this.#index = suivant;

      window.clearTimeout(this.#fin);
      this.#fin = window.setTimeout(() => {
        // L'entrante couvre tout : la sortante est masquee d'un coup, sans transition,
        // sinon elle reapparait le temps de son propre fondu.
        sortante.classList.add('sans-transition');
        sortante.classList.remove('est-active');
        void sortante.offsetHeight;
        sortante.classList.remove('sans-transition');
      }, fondu);
    }, delai);
  }

  disconnectedCallback() {
    window.clearInterval(this.#minuteur);
    window.clearTimeout(this.#fin);
  }

  /**
   * Remplace le <template> d'une vue par la photo qu'il contient.
   * @param {Element | undefined} vue
   */
  #deplier(vue) {
    const modele = vue?.querySelector(':scope > template[data-ee-differe]');
    if (modele instanceof HTMLTemplateElement) modele.replaceWith(modele.content);
  }
}

/**
 * Marque l'en-tete tant que le hero passe derriere lui.
 *
 * Horizon deduit cet etat d'un observateur qui guette le pixel dont l'en-tete
 * colle depasse en haut de l'ecran. Ce reperage d'un seul pixel s'est revele
 * instable : l'etat pouvait s'inverser selon le sens du defilement, et la
 * barre restait transparente au milieu de la page, donc invisible.
 *
 * On observe donc directement le hero, ce qui est exactement la question posee :
 * « le decor est-il encore derriere la barre ? »
 */
const suivreEnteteSurHero = () => {
  const entete = document.querySelector('#header-component');
  const hero = document.querySelector('.ee-hero');

  if (!entete || !hero) return;

  const evaluer = () => {
    // Le decor est-il encore sous la barre ? On compare le bas du hero a la
    // hauteur de l'en-tete, une marge pres pour eviter le clignotement au pixel.
    const surDecor = hero.getBoundingClientRect().bottom > entete.offsetHeight + 4;
    entete.classList.toggle('ee-entete-sur-decor', surDecor);
  };

  // Mesure directe plutot que differee a la frame suivante : une frame de
  // retard suffisait a laisser la barre transparente au mauvais endroit.
  // Un seul rectangle est lu par evenement, le cout est negligeable.
  evaluer();
  window.addEventListener('scroll', evaluer, { passive: true });
  window.addEventListener('resize', evaluer, { passive: true });
};

/**
 * Menu horizontal de l'en-tete, visible seulement une fois le hero passe.
 *
 * Les liens ne sont pas recopies a la main : on reprend ceux du tiroir, pour
 * que la barre suive toujours le menu defini dans l'admin. Seuls les intitules
 * listes ici sont repris, les autres restent au tiroir.
 */
const MENU_ENTETE = ['accueil', 'boutique', 'contact', 'à propos'];

const poserMenuEntete = () => {
  const entete = document.querySelector('#header-component');
  if (!entete || entete.querySelector('.ee-entete-menu')) return;

  const colonne = entete.querySelector('.header__column--left');
  const liens = [...entete.querySelectorAll('.menu-drawer__menu-item--mainlist')];
  if (!colonne || liens.length === 0) return;

  /* Le libelle seul : les liens du tiroir portent aussi une petite mention
     cachee (« Chapitre 1 », « 4 imprimés ») qui ne doit ni empecher de les
     reconnaitre ni passer dans le menu de l'en-tete. */
  const libelle = (a) => {
    const copie = a.cloneNode(true);
    copie.querySelectorAll('.ee-menu-indice').forEach((m) => m.remove());
    return copie.textContent.replace(/\s+/g, ' ').trim();
  };
  const normaliser = (t) => t.toLowerCase();
  const retenus = MENU_ENTETE.map((voulu) =>
    liens.find((a) => normaliser(libelle(a)) === voulu)
  ).filter(Boolean);

  if (retenus.length === 0) return;

  const nav = document.createElement('nav');
  nav.className = 'ee-entete-menu';
  nav.setAttribute('aria-label', 'Menu principal');

  for (const source of retenus) {
    const lien = document.createElement('a');
    lien.className = 'ee-entete-menu__lien';
    lien.href = source.getAttribute('href') || '#';
    lien.textContent = libelle(source);
    nav.appendChild(lien);
  }

  colonne.appendChild(nav);
};

/**
 * Le menu ci-dessus ne fait pas partie du code de la section : quand Horizon
 * redessine l'en-tete, il disparait. C'est le cas a chaque ajout au panier,
 * le tiroir du panier etant rendu par la section de l'en-tete : les liens
 * s'effacaient jusqu'au chargement de la page suivante.
 *
 * Un observateur guette donc l'en-tete et repose le menu des qu'il manque.
 * poserMenuEntete ne fait rien quand le menu est deja la : la pose provoque
 * bien un nouveau passage de l'observateur, mais il s'arrete aussitot, sans
 * boucle ni doublon.
 */
/** @type {MutationObserver | null} */
let veilleEntete = null;

/** @type {Element | null} */
let enteteVeille = null;

const veillerMenuEntete = () => {
  const entete = document.querySelector('#header-component');

  // Meme en-tete qu'au passage precedent : l'observateur est deja en place
  if (!entete || entete === enteteVeille) return;

  // Personnalisateur : la section a ete remplacee, on suit la nouvelle
  veilleEntete?.disconnect();
  enteteVeille = entete;
  veilleEntete = new MutationObserver(() => {
    if (!entete.querySelector('.ee-entete-menu')) poserMenuEntete();
  });
  veilleEntete.observe(entete, { childList: true, subtree: true });
};

/**
 * Cartes produit : fait defiler la premiere photo de chaque coloris.
 *
 * Le theme marque deja ces vues d'un attribut « variant-image » et n'affiche
 * que la premiere. Plutot que de bousculer son diaporama, on echange la source
 * de l'image visible : aucune mise en page n'est touchee, et les fleches du
 * carrousel continuent de fonctionner normalement.
 */
const DELAI_COULEURS = 2600;
const FONDU_COULEURS = 320;

const faireDefilerLesColoris = () => {
  if (reduceMotion()) return;

  document.querySelectorAll('.card-gallery').forEach((galerie) => {
    if (galerie.dataset.eeColoris) return;

    const vues = [...galerie.querySelectorAll('slideshow-slide[variant-image] img')];
    if (vues.length < 2) return;

    const cible = vues[0];
    const sources = vues.map((img) => ({
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset'),
    }));

    // Prechargement : sans lui, l'echange laisse un blanc le temps du telechargement
    sources.forEach((source) => {
      const avance = new Image();
      if (source.srcset) avance.srcset = source.srcset;
      if (source.src) avance.src = source.src;
    });

    galerie.dataset.eeColoris = 'actif';
    cible.classList.add('ee-coloris-vue');

    let index = 0;
    let attente = 0;

    const minuteur = window.setInterval(() => {
      // Onglet en arriere-plan, ou visiteur qui survole la carte : on patiente
      if (document.hidden || galerie.matches(':hover')) return;

      index = (index + 1) % sources.length;
      cible.classList.add('ee-coloris-sortie');

      window.clearTimeout(attente);
      attente = window.setTimeout(() => {
        const source = sources[index];
        if (source.srcset) cible.setAttribute('srcset', source.srcset);
        if (source.src) cible.setAttribute('src', source.src);
        cible.classList.remove('ee-coloris-sortie');
      }, FONDU_COULEURS);
    }, DELAI_COULEURS);

    // Le minuteur s'arrete si la carte quitte la page
    const veille = new MutationObserver(() => {
      if (!document.body.contains(galerie)) {
        window.clearInterval(minuteur);
        window.clearTimeout(attente);
        veille.disconnect();
      }
    });
    veille.observe(document.body, { childList: true, subtree: true });
  });
};

/**
 * Volets deroulants : ouverture et fermeture animees.
 *
 * <details> n'anime rien nativement : le contenu apparait et disparait d'un
 * coup. On garde tout le comportement natif (clavier, recherche dans la page,
 * ancres) et on ne fait qu'habiller la transition de hauteur, en repoussant
 * la fermeture reelle du volet a la fin de l'animation.
 */
const DUREE_VOLET = 300;
const COURBE_VOLET = 'cubic-bezier(0.33, 1, 0.68, 1)';

const animerLesVolets = (racine = document) => {
  racine.querySelectorAll('.ee-details__volet').forEach((volet) => {
    if (volet.dataset.eeVolet) return;

    const resume = volet.querySelector('.ee-details__bouton');
    const corps = volet.querySelector('.ee-details__contenu');
    if (!resume || !(corps instanceof HTMLElement)) return;

    volet.dataset.eeVolet = 'anime';

    /** @type {Animation | null} */
    let animation = null;

    const animer = (ouvrir) => {
      animation?.cancel();

      // A l'ouverture, le contenu n'a pas encore de hauteur mesurable : le
      // volet doit etre ouvert avant qu'on lise sa hauteur naturelle.
      const depart = ouvrir ? 0 : corps.offsetHeight;
      if (ouvrir) volet.open = true;
      const arrivee = ouvrir ? corps.scrollHeight : 0;

      corps.style.overflow = 'hidden';

      animation = corps.animate(
        {
          height: [`${depart}px`, `${arrivee}px`],
          opacity: ouvrir ? [0, 1] : [1, 0],
        },
        { duration: DUREE_VOLET, easing: COURBE_VOLET }
      );

      animation.onfinish = () => {
        // La fermeture n'a lieu qu'ici : sinon le contenu disparaitrait
        // avant meme que l'animation commence.
        volet.open = ouvrir;
        corps.style.overflow = '';
        animation = null;
      };
    };

    resume.addEventListener('click', (evenement) => {
      // Mouvement reduit : on laisse le navigateur faire, sans transition
      if (reduceMotion()) return;
      evenement.preventDefault();
      animer(!volet.open);
    });
  });
};

/**
 * Fiche produit : le clic sur la photo passe a la suivante.
 *
 * La vue agrandie livree avec le theme ouvrait l'image a sa taille reelle,
 * plus de 2700px de haut : on n'y voyait plus qu'un morceau de tissu, sans
 * moyen evident de revenir en arriere. Elle est desactivee, et le clic fait
 * desormais ce que le visiteur attend d'une galerie : avancer d'une photo.
 * On reutilise la fleche « suivant » du theme plutot que de piloter le
 * carrousel nous-memes, pour que compteur et fleches restent d'accord.
 */
const activerClicPhoto = () => {
  const galerie = document.querySelector('.product-information__grid .product-information__media');
  if (!galerie || galerie.dataset.eeClic) return;

  const suivant = galerie.querySelector('.slideshow-control--next');
  if (!suivant) return;

  galerie.dataset.eeClic = 'actif';

  galerie.querySelectorAll('.product-media-container').forEach((vue) => {
    vue.classList.add('ee-photo-cliquable');
    vue.addEventListener('click', (evenement) => {
      // Les fleches et le compteur gardent leur propre comportement
      if (evenement.target.closest('.slideshow-control, button, a')) return;
      suivant.click();
    });
  });
};

/**
 * Pages legales : chaque chapitre devient un volet.
 *
 * Shopify rend ces pages hors du theme, d'un seul tenant : la politique de
 * confidentialite faisait plus de 7000px de haut, les conditions generales
 * plus de 10000. Les chapitres deviennent des volets deroulants, comme sur la
 * fiche produit : la page tient dans un ecran, et chacun ouvre celui qui le
 * concerne.
 *
 * Rien n'est supprime : tout le texte reste dans la page, donc lisible par
 * les moteurs de recherche comme par les lecteurs d'ecran.
 */
const construireSection = (titre, noeuds, id) => {
  const section = document.createElement('section');
  section.className = 'ee-legal__chap';
  section.id = id;

  const h = document.createElement('h2');
  h.className = 'ee-legal__chap-titre';
  h.textContent = titre;

  const contenu = document.createElement('div');
  contenu.className = 'ee-legal__chap-corps rte';
  noeuds.forEach((noeud) => contenu.appendChild(noeud));

  section.append(h, contenu);
  return section;
};

/**
 * Premier cas : la page est structuree par des intertitres. C'est la forme de
 * la politique de confidentialite.
 */
const chapitresDepuisTitres = (corps) => {
  const conteneur = corps.querySelector('h2')?.parentElement;
  if (!conteneur) return null;

  const titres = [...conteneur.children].filter((noeud) => noeud.tagName === 'H2');
  if (titres.length < 3) return null;

  const chapitres = titres.map((titre) => {
    const noeuds = [];
    let suivant = titre.nextElementSibling;
    while (suivant && suivant.tagName !== 'H2') {
      const aDeplacer = suivant;
      suivant = suivant.nextElementSibling;
      noeuds.push(aDeplacer);
    }
    return { titre: titre.textContent.trim(), noeuds, origine: titre };
  });

  return { conteneur, chapitres };
};

/**
 * Second cas : aucun intertitre, tout tient dans un seul paragraphe ou les
 * titres sont de simples passages en gras separes par des sauts de ligne.
 * C'est la forme des conditions generales livrees par Shopify : 26 titres en
 * gras et 113 <br> dans un unique paragraphe.
 */
const chapitresDepuisGras = (corps) => {
  const para = [...corps.querySelectorAll('p')].find(
    (bloc) => bloc.querySelectorAll('strong').length >= 3
  );
  if (!para) return null;

  const chapitres = [];
  let courant = null;

  [...para.childNodes].forEach((noeud) => {
    const estTitre =
      noeud.nodeType === Node.ELEMENT_NODE &&
      noeud.tagName === 'STRONG' &&
      noeud.textContent.trim().length > 0;

    if (estTitre) {
      courant = { titre: noeud.textContent.trim(), noeuds: [], origine: noeud };
      chapitres.push(courant);
      return;
    }

    // Les sauts de ligne qui ouvrent un chapitre ne servent plus a rien une
    // fois le texte range dans son volet
    if (!courant) return;
    const vide =
      (noeud.nodeType === Node.TEXT_NODE && !noeud.textContent.trim()) ||
      (noeud.nodeType === Node.ELEMENT_NODE && noeud.tagName === 'BR');
    if (vide && courant.noeuds.length === 0) return;

    courant.noeuds.push(noeud);
  });

  if (chapitres.length < 3) return null;
  return { conteneur: para.parentElement, chapitres, para };
};

/*
 * Navigation entre les pages d'informations, posee au-dessus du titre : on passe
 * d'une politique a l'autre sans repasser par le pied de page.
 */
/*
 * Au report dans Shopify, ces pages ne sont plus des fichiers de l'apercu :
 * la boutique les sert sous /policies/, avec des adresses fixes quelle que
 * soit la langue de la boutique. Les sept ont ete verifiees sur la boutique.
 */
const PAGES_LEGALES = [
  ['/policies/terms-of-sale', 'Conditions de vente'],
  ['/policies/terms-of-service', "Conditions d'utilisation"],
  ['/policies/privacy-policy', 'Confidentialité'],
  ['/policies/shipping-policy', 'Expédition'],
  ['/policies/refund-policy', 'Remboursements'],
  ['/policies/legal-notice', 'Mentions légales'],
  ['/policies/contact-information', 'Coordonnées'],
];

const poserNavLegale = () => {
  const conteneur = document.querySelector('.shopify-policy__container');
  const titre = document.querySelector('.shopify-policy__title');
  if (!conteneur || !titre || conteneur.querySelector('.ee-legal-nav')) return;

  const ici = (location.pathname.replace(/\/$/, '').split('/').pop() || '').toLowerCase();
  const nav = document.createElement('nav');
  nav.className = 'ee-legal-nav';
  nav.setAttribute('aria-label', "Pages d'informations");

  PAGES_LEGALES.forEach(([adresse, label]) => {
    const a = document.createElement('a');
    a.className = 'ee-legal-nav__lien';
    a.href = adresse;
    a.textContent = label;
    // On compare le dernier segment : /policies/terms-of-sale -> terms-of-sale
    if (adresse.split('/').pop() === ici) {
      a.classList.add('is-current');
      a.setAttribute('aria-current', 'page');
    }
    nav.appendChild(a);
  });

  titre.parentNode.insertBefore(nav, titre);
};

/* Surligne dans le sommaire le chapitre a l'ecran */
const suivreLeSommaire = (nav, sections) => {
  const liens = new Map();
  nav.querySelectorAll('a[href^="#"]').forEach((a) => liens.set(a.getAttribute('href').slice(1), a));
  if (!('IntersectionObserver' in window)) return;
  let actif = null;
  const io = new IntersectionObserver(
    (entrees) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        const a = liens.get(e.target.id);
        if (!a || a === actif) return;
        if (actif) actif.classList.remove('is-active');
        a.classList.add('is-active');
        actif = a;
      });
    },
    { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
  );
  sections.forEach((s) => io.observe(s));
};

const replierPagesLegales = () => {
  const corps = document.querySelector('.shopify-policy__body');
  if (!corps || corps.dataset.eeReplie) return;

  const decoupe = chapitresDepuisTitres(corps) || chapitresDepuisGras(corps);
  if (!decoupe) return;

  corps.dataset.eeReplie = 'oui';

  const bloc = document.createElement('div');
  bloc.className = 'ee-legal';

  const nav = document.createElement('nav');
  nav.className = 'ee-legal__somm';
  nav.setAttribute('aria-label', 'Sommaire');
  const navTitre = document.createElement('p');
  navTitre.className = 'ee-legal__somm-titre';
  navTitre.textContent = 'Sommaire';
  const liste = document.createElement('ol');
  liste.className = 'ee-legal__somm-liste';
  nav.append(navTitre, liste);

  const corpsLegal = document.createElement('div');
  corpsLegal.className = 'ee-legal__corps';

  const sections = [];
  decoupe.chapitres.forEach(({ titre, noeuds, origine }, i) => {
    const id = 'ee-chap-' + i;
    const section = construireSection(titre, noeuds, id);
    corpsLegal.appendChild(section);
    sections.push(section);

    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = '#' + id;
    a.textContent = titre;
    li.appendChild(a);
    liste.appendChild(li);

    origine.remove();
  });

  // Le paragraphe d'origine ne contient plus que des sauts de ligne orphelins
  if (decoupe.para && !decoupe.para.textContent.trim()) decoupe.para.remove();

  bloc.append(nav, corpsLegal);
  decoupe.conteneur.appendChild(bloc);
  suivreLeSommaire(nav, sections);
};

/*
 * Recherche : etat par defaut « Explorer » quand le champ est vide (raccourcis
 * vers les pages cles), pour ne pas ouvrir sur une boite vide.
 *
 * Adresses de la boutique, en chemins absolus : les noms de fichiers de
 * l'apercu statique (pantalons.html...) menaient a des pages introuvables.
 */
const LIENS_RECHERCHE = [
  ['/collections/pantalons', 'Tous les pantalons'],
  ['/collections', 'Collections'],
  ['/pages/a-propos', 'La marque'],
  ['/pages/contact', 'Contact'],
];

/*
 * Le theme redessine le contenu du panneau : produits vus recemment a
 * l'ouverture, resultats a chaque frappe, remise a zero a la fermeture. Le
 * bloc « Explorer », absent du code du serveur, en est retire a chaque fois.
 * Deux precautions :
 *   - le bloc porte un identifiant. Sans lui, le theme le recyclait en liste
 *     de resultats (deux <div> sans identifiant passent pour le meme
 *     element), et masquer le bloc pendant la saisie masquait alors les
 *     resultats eux-memes ;
 *   - un observateur le repose en tete du panneau des qu'il en est retire.
 * Le bloc est construit une seule fois par panneau : l'observateur repose
 * toujours le meme, ses ecouteurs ne sont jamais doubles, et la pose ne
 * relance pas l'observateur indefiniment (bloc deja en place, rien a faire).
 */
/** @type {MutationObserver | null} */
let veilleRecherche = null;

/** @type {Element | null} */
let contenuVeille = null;

const enrichirRecherche = () => {
  const contenu = document.querySelector('.predictive-search-form__content');
  const input = document.querySelector('#cmdk-input') ||
    document.querySelector('.predictive-search .search-input') ||
    document.querySelector('.predictive-search input[name="q"]');

  // Meme panneau qu'au passage precedent : bloc et observateur deja en place
  if (!contenu || !input || contenu === contenuVeille) return;

  const def = document.createElement('div');
  def.id = 'ee-search-default';
  def.className = 'ee-search-default';

  const titre = document.createElement('p');
  titre.className = 'ee-search-default__titre';
  titre.textContent = 'Explorer';

  const liens = document.createElement('div');
  liens.className = 'ee-search-default__liens';
  LIENS_RECHERCHE.forEach(([href, label]) => {
    const a = document.createElement('a');
    a.className = 'ee-search-default__lien';
    a.href = href;
    a.textContent = label;
    liens.appendChild(a);
  });

  def.append(titre, liens);

  const maj = () => { def.hidden = input.value.trim().length > 0; };
  const poser = () => {
    if (def.parentNode === contenu) return;
    contenu.insertBefore(def, contenu.firstChild);
    maj();
  };

  input.addEventListener('input', maj);
  input.addEventListener('focus', maj);
  const modale = input.closest('dialog-component, dialog, .search-modal');
  if (modale) modale.addEventListener('click', () => setTimeout(maj, 0));

  // Personnalisateur : la section a ete remplacee, on suit le nouveau panneau
  veilleRecherche?.disconnect();
  contenuVeille = contenu;
  contenu.querySelectorAll('.ee-search-default').forEach((ancien) => ancien.remove());
  poser();
  veilleRecherche = new MutationObserver(poser);
  veilleRecherche.observe(contenu, { childList: true });
};

const demarrerEntete = () => {
  poserMenuEntete();
  veillerMenuEntete();
  // Barre ivoire pleine sur l'accueil aussi depuis le 2026-09-26 (Charlotte :
  // « une bande avec le menu plutot que les bulles ») : plus de decor a suivre.
  // suivreEnteteSurHero();
  faireDefilerLesColoris();
  animerLesVolets();
  activerClicPhoto();
  poserNavLegale();
  replierPagesLegales();
  enrichirRecherche();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', demarrerEntete, { once: true });
} else {
  demarrerEntete();
}

// Personnalisateur : un reglage modifie redessine sa section sans recharger
// la page. Chaque etape ci-dessus reconnait ce qu'elle a deja pose, on peut
// donc tout relancer sans rien doubler.
document.addEventListener('shopify:section:load', demarrerEntete);

if (!customElements.get('ee-reveal')) customElements.define('ee-reveal', EmilyReveal);
if (!customElements.get('ee-diaporama')) customElements.define('ee-diaporama', EmilyDiaporama);
if (!customElements.get('ee-lookbook')) customElements.define('ee-lookbook', EmilyLookbook);
if (!customElements.get('ee-wipe')) customElements.define('ee-wipe', EmilyWipe);

/*
 * Arrivee sur la fiche produit avec un coloris demande.
 *
 * Les cartes de la page boutique menent a « /products/... #coloris=Nom ». Le
 * navigateur n'appelle pas le serveur pour une ancre : c'est donc ici qu'on
 * coche le coloris voulu. Deux moments a couvrir :
 *   - la page s'ouvre deja sur l'ancre, et « hashchange » ne se declenche pas ;
 *   - on clique sur une carte alors qu'on est deja sur la fiche.
 * Le selecteur de variantes est monte apres coup par le theme, on retente donc
 * quelques fois avant d'abandonner.
 */
const coloriDemande = () => {
  if (!location.hash.startsWith('#coloris=')) return '';
  return decodeURIComponent(location.hash.slice('#coloris='.length)).trim();
};

const cocherColoris = (voulu) => {
  if (!voulu) return false;
  const choix = [...document.querySelectorAll('.variant-option input[type="radio"]')].find(
    (entree) => entree.value.trim() === voulu
  );
  if (!choix) return false;
  if (!choix.checked) choix.click();
  return true;
};

const suivreColoris = (voulu, essaisMax = 20) => {
  let essais = 0;
  const horloge = window.setInterval(() => {
    essais += 1;
    if (cocherColoris(voulu) || essais >= essaisMax) window.clearInterval(horloge);
  }, 150);
};

if (document.querySelector('.variant-option, product-form-component')) {
  suivreColoris(coloriDemande());
  window.addEventListener('hashchange', () => {
    const voulu = coloriDemande();
    if (!voulu) return;
    if (!cocherColoris(voulu)) suivreColoris(voulu, 10);
    const galerie = document.querySelector('media-gallery');
    if (!galerie) return;
    const entete = document.querySelector('#header-component');
    const marge = entete ? entete.getBoundingClientRect().height + 16 : 80;
    const haut = galerie.getBoundingClientRect().top + window.pageYOffset - marge;
    window.scrollTo({ top: Math.max(haut, 0), behavior: 'smooth' });
  });
}
